import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useStore } from "../lib/storeContext";
import { resolveAsset } from "../lib/assetResolver";
import { Breadcrumbs, EmptyState, Spinner, StatPill } from "./ui";
import { DeleteButton } from "./DeleteButton";
import { FloorplanSvg } from "../floorplan/FloorplanSvg";
// three.js is heavy — load the 3D dollhouse only when a scan is opened.
const RoomDollhouse = lazy(() =>
  import("../dollhouse/RoomDollhouse").then((m) => ({ default: m.RoomDollhouse })),
);
import { buildScanGeometry, wallThicknessFromScan } from "../geometry/scanGeometry";
import type { ScanGeometry } from "../geometry/types";
import type { CapturedRoomJSON } from "../geometry/types";
import { formatArea, formatLength } from "../geometry/format";
import { useMeasurementSystem } from "../geometry/format";
import type { RoomPayload, ScanPayload } from "../lib/types";
import { listSplats, getSplatJob } from "../lib/apiClient";

type Tab = "plan" | "3d" | "details";

export function ScanDetailView() {
  const { scanId = "" } = useParams();
  const navigate = useNavigate();
  const { store, rev, loading } = useStore();
  const [tab, setTab] = useState<Tab>("plan");
  const [geometry, setGeometry] = useState<ScanGeometry | null>(null);
  const [geoErr, setGeoErr] = useState<string | null>(null);
  const { system, toggle } = useMeasurementSystem();
  void rev;

  const scan = store.get<ScanPayload>("SCAN", scanId);
  const room = scan?.parentId ? store.get<RoomPayload>("ROOM", scan.parentId) : undefined;
  const roomJsonKey = scan?.assets.find((a) => a.kind === "room-json")?.s3Key;

  useEffect(() => {
    if (!roomJsonKey) return;
    let cancelled = false;
    setGeometry(null);
    setGeoErr(null);
    resolveAsset(roomJsonKey)
      .then(async (url) => {
        if (!url) throw new Error("floorplan data unavailable");
        const resp = await fetch(url);
        const json = (await resp.json()) as CapturedRoomJSON;
        if (!cancelled) setGeometry(buildScanGeometry(json, scan ? wallThicknessFromScan(scan.payload) : undefined));
      })
      .catch((e: unknown) => {
        if (!cancelled) setGeoErr(e instanceof Error ? e.message : "failed to load floorplan");
      });
    return () => {
      cancelled = true;
    };
  }, [roomJsonKey]);

  // W4: a wall-cropped photoreal splat paired to THIS scan (splatJob.roomScanId === scanId). When one
  // exists, the 3D tab offers a "Photoreal" overlay of the faithful gaussian splat on the dollhouse.
  // All photoreal splats paired to THIS scan (a room can have e.g. a video AND a photos capture).
  // model-cropped.ply is baked into the room frame server-side, so the viewer renders it at identity.
  const [splats, setSplats] = useState<{ url: string; label: string }[]>([]);
  // splat→mesh: the watertight textured polygon room mesh (room-mesh.glb) for the dollhouse "Mesh" view.
  const [meshUrl, setMeshUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!scanId) return;
    let cancelled = false;
    setSplats([]);
    setMeshUrl(null);
    (async () => {
      try {
        const jobs = await listSplats();
        const paired = jobs.filter((j) => j.status === "completed" && j.roomScanId === scanId);
        const out: { url: string; label: string }[] = [];
        let mesh: string | null = null;
        for (const j of paired) {
          const detail = await getSplatJob(j.splatId);
          if (!mesh && detail.meshUrl) mesh = detail.meshUrl; // first paired job that produced a mesh
          const cropped = detail.models?.find((m) => m.id === "model-cropped.ply");
          if (!cropped) continue;
          const label = j.inputKind === "video" ? "Video" : j.inputKind === "images" || j.source === "photos" ? "Photos" : "Splat";
          out.push({ url: cropped.url, label });
        }
        if (!cancelled) { setSplats(out); setMeshUrl(mesh); }
      } catch { /* no paired splats / not signed in — 3D tab just stays boxes */ }
    })();
    return () => { cancelled = true; };
  }, [scanId]);

  if (loading) return <Spinner />;
  if (!scan) return <EmptyState title="Scan not found" icon="🔍" />;
  const sp = scan.payload;

  return (
    <div className="space-y-5">
      <Breadcrumbs
        items={[
          { label: "Projects", to: "/" },
          { label: room?.payload.name ?? "Room", to: room ? `/rooms/${room.id}` : undefined },
          { label: "Scan" },
        ]}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-ink">{room?.payload.name ?? "Scan"} — floorplan & 3D</h1>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full bg-cream p-1 text-sm">
            {(["plan", "3d", "details"] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`rounded-full px-4 py-1.5 font-medium capitalize transition ${
                  tab === t ? "bg-white text-accent shadow-card" : "text-muted"
                }`}
              >
                {t === "3d" ? "3D" : t}
              </button>
            ))}
          </div>
          {(sp.splatModelFileName || sp.splatJobId) && (
            <Link to={`/scans/${scanId}/splat`} className="btn-warm px-3 py-1.5 text-sm">
              ✦ Inspect model
            </Link>
          )}
          <button className="btn-ghost text-sm" onClick={toggle}>
            {system === "metric" ? "Metric" : "Imperial"}
          </button>
          <DeleteButton
            type="SCAN"
            id={scanId}
            what="scan"
            onDeleted={() => navigate(room ? `/rooms/${room.id}` : "/")}
          />
        </div>
      </div>

      {tab === "plan" && (
        <div className="card overflow-hidden p-2">
          {geoErr ? (
            <EmptyState title="Floorplan unavailable" subtitle={geoErr} icon="⚠️" />
          ) : !geometry ? (
            <Spinner label="Rendering floorplan…" />
          ) : (
            <FloorplanSvg geometry={geometry} system={system} />
          )}
        </div>
      )}

      {tab === "3d" && (
        <div className="card overflow-hidden">
          {geoErr ? (
            <EmptyState title="3D unavailable" subtitle={geoErr} icon="⚠️" />
          ) : !geometry ? (
            <Spinner label="Building 3D…" />
          ) : (
            <Suspense fallback={<Spinner label="Loading 3D…" />}>
              <RoomDollhouse geometry={geometry} splats={splats} meshUrl={meshUrl ?? undefined} />
            </Suspense>
          )}
        </div>
      )}

      {tab === "details" && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <StatPill label="Floor area" value={formatArea(sp.floorAreaSquareMeters, system)} />
          <StatPill label="Ceiling" value={formatLength(sp.ceilingHeightMeters, system)} />
          <StatPill label="Perimeter" value={formatLength(sp.perimeterMeters, system)} />
          <StatPill label="Walls" value={String(sp.wallCount)} />
          <StatPill label="Doors" value={String(sp.doorCount)} />
          <StatPill label="Windows" value={String(sp.windowCount)} />
          <StatPill label="Openings" value={String(sp.openingCount)} />
          <StatPill label="Objects" value={String(sp.objectCount)} />
        </div>
      )}
    </div>
  );
}
